import React, { useRef, useState } from 'react';
import { bool, func, object, shape } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isUploadImageOverLimitError } from '../../util/errors';

import {
  Button,
  PrimaryButton,
  SecondaryButton,
  IconSpinner,
  AspectRatioWrapper,
  VideoFromFile,
  VideoPlayer,
  InfoTooltip,
  Icon,
} from '../../components';

// Import ControlMenuVideo from EditLiveRoomBehindTheScenesForm
import { ControlMenuVideo } from '../../forms/EditLiveRoomBehindTheScenesForm/ControlMenu';

import css from './BehindTheScenesUploadForm.module.css';

const ACCEPT_VIDEOS = 'video/*';

const VideoItem = ({
  video,
  onRemove,
  aspectWidth,
  aspectHeight,
  controlMenuOpen,
  onToggleControlMenu,
}) => {
  const isUploading = video.uploading;
  const hasError = video.error;

  // Match the logic from ListingVideo component
  if (video.file && !video.src) {
    // Add control menu only when the video has been uploaded and can be removed
    const controlMenu =
      video.videoId && !isUploading ? (
        <ControlMenuVideo
          video={video}
          isMenuOpen={controlMenuOpen?.id === video.id || controlMenuOpen?.id === video.videoId}
          onToggleMenu={onToggleControlMenu}
          onRemoveVideo={() => onRemove(video.id)}
        />
      ) : null;

    // While video is uploading we show overlay on top of thumbnail
    const uploadingOverlay = isUploading ? (
      <div className={css.thumbnailLoading}>
        <IconSpinner className={css.spinnerIcon} />
      </div>
    ) : null;

    const errorOverlay = hasError ? (
      <div className={css.thumbnailLoading}>
        <FormattedMessage id="BehindTheScenesUploadForm.uploadError" />
      </div>
    ) : null;

    return (
      <VideoFromFile
        id={video.id}
        className={css.thumbnail}
        file={video.file}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
      >
        {controlMenu}
        {uploadingOverlay}
        {errorOverlay}
      </VideoFromFile>
    );
  } else if (video.src) {
    // This handles uploaded videos with src URLs
    const classes = classNames(css.root, css.thumbnail);

    return (
      <div className={classes}>
        <div className={css.wrapper}>
          <VideoPlayer
            width={aspectWidth}
            height={aspectHeight}
            video={video}
            settings={{ playerTime: false, hoverPlay: false }}
          />
          <ControlMenuVideo
            video={video}
            isMenuOpen={controlMenuOpen?.id === video.id || controlMenuOpen?.id === video.videoId}
            onToggleMenu={onToggleControlMenu}
            onRemoveVideo={() => onRemove(video.id)}
          />
        </div>
      </div>
    );
  } else {
    // Fallback for videos without file or src - show video info
    return (
      <div className={css.thumbnail}>
        <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
          <div className={css.videoInfo}>
            <Icon name="video" size={32} className={css.videoIcon} />
            <span className={css.videoName}>{video.name || 'Video'}</span>
          </div>
        </AspectRatioWrapper>
        {!isUploading && (
          <ControlMenuVideo
            video={video}
            isMenuOpen={controlMenuOpen?.id === video.id || controlMenuOpen?.id === video.videoId}
            onToggleMenu={onToggleControlMenu}
            onRemoveVideo={() => onRemove(video.id)}
          />
        )}
      </div>
    );
  }
};

const BehindTheScenesUploadForm = props => {
  const {
    intl,
    listing,
    uploadedVideos,
    onVideoUpload,
    onRemoveVideo,
    onSubmit,
    uploadInProgress,
    uploadVideoError,
    updateInProgress,
    updateError,
    onClose,
  } = props;

  const videoInputRef = useRef(null);
  const [localVideos, setLocalVideos] = useState([]);
  const [controlMenuVideoOpen, toggleControlMenuVideoOpen] = useState(null);

  const existingBehindTheScenes = listing?.attributes?.publicData?.behindTheScenes || [];

  const uploadedVideosList = Object.entries(uploadedVideos).map(([id, data]) => ({
    id,
    ...data,
  }));

  const allNewVideos = [...localVideos, ...uploadedVideosList];
  const hasNewVideos = allNewVideos.length > 0;
  const isUploadingAny = uploadedVideosList.some(v => v.uploading);

  const handleVideoSelect = async e => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      const tempId = `${file.name}_${Date.now()}`;

      // Add to local state immediately
      setLocalVideos(prev => [...prev, { id: tempId, name: file.name, uploading: true }]);

      // Upload the video
      try {
        onVideoUpload({
          id: tempId,
          file,
          listingId: listing.id.uuid,
        });

        // Remove from local state immediately (it's now in Redux uploadedVideos)
        setLocalVideos(prev => prev.filter(v => v.id !== tempId));
      } catch (error) {
        // Update local state to show error
        setLocalVideos(prev =>
          prev.map(v => (v.id === tempId ? { ...v, uploading: false, error: true } : v))
        );
      }
    }

    // Reset input
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleRemoveLocalVideo = videoId => {
    setLocalVideos(prev => prev.filter(v => v.id !== videoId));
  };

  const handleRemoveUploadedVideo = videoId => {
    onRemoveVideo(videoId);
  };

  const handleSubmit = () => {
    // Get successfully uploaded videos
    const newVideos = uploadedVideosList
      .filter(v => !v.uploading && !v.error)
      .map(({ id, file, uploading, ...videoData }) => videoData);

    // Combine with existing behind the scenes
    const allBehindTheScenes = [...existingBehindTheScenes, ...newVideos];

    // Call onSubmit with updated behind the scenes
    onSubmit(allBehindTheScenes);
  };

  const uploadOverLimit = isUploadImageOverLimitError(uploadVideoError);

  // Calculate aspect ratios like in EditLiveRoomBehindTheScenesForm
  const getAspectWidthForVideoUpload = hasFiles => (hasFiles ? 5 : 6);
  const getAspectHeightForVideoUpload = hasFiles => (hasFiles ? 7 : 4);

  const aspectWidthForVideoUpload = getAspectWidthForVideoUpload(hasNewVideos);
  const aspectHeightForVideoUpload = getAspectHeightForVideoUpload(hasNewVideos);

  return (
    <FinalForm
      onSubmit={handleSubmit}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={css.form}>
          <div className={css.formContent}>
            <div className={css.uploadSection}>
              <input
                ref={videoInputRef}
                type="file"
                accept={ACCEPT_VIDEOS}
                multiple
                onChange={handleVideoSelect}
                className={css.fileInput}
                id="video-upload"
                disabled={isUploadingAny || updateInProgress}
              />
              <label htmlFor="video-upload" className={css.uploadButton}>
                <Icon name="video" size={48} className={css.uploadIcon} />
                <span className={css.uploadText}>
                  <FormattedMessage id="BehindTheScenesUploadForm.selectVideos" />
                  <InfoTooltip
                    items={[
                      intl.formatMessage({ id: 'BehindTheScenesUploadForm.tip1' }),
                      intl.formatMessage({ id: 'BehindTheScenesUploadForm.tip2' }),
                      intl.formatMessage({ id: 'BehindTheScenesUploadForm.tip3' }),
                    ]}
                  />
                </span>
                <span className={css.uploadHint}>
                  <FormattedMessage id="BehindTheScenesUploadForm.videoTypes" />
                </span>
              </label>
            </div>

            {hasNewVideos && (
              <div className={css.filesFieldArray}>
                {allNewVideos.map(video => (
                  <VideoItem
                    key={video.id}
                    video={video}
                    aspectWidth={aspectWidthForVideoUpload}
                    aspectHeight={aspectHeightForVideoUpload}
                    controlMenuOpen={controlMenuVideoOpen}
                    onToggleControlMenu={toggleControlMenuVideoOpen}
                    onRemove={
                      video.uploading
                        ? null
                        : localVideos.find(v => v.id === video.id)
                        ? handleRemoveLocalVideo
                        : handleRemoveUploadedVideo
                    }
                  />
                ))}
              </div>
            )}

            {uploadOverLimit && (
              <p className={css.error}>
                <FormattedMessage id="BehindTheScenesUploadForm.videoUploadFailed.uploadOverLimit" />
              </p>
            )}

            {uploadVideoError && !uploadOverLimit && (
              <p className={css.error}>
                <FormattedMessage id="BehindTheScenesUploadForm.videoUploadFailed.uploadFailed" />
              </p>
            )}

            {updateError && (
              <p className={css.error}>
                <FormattedMessage id="BehindTheScenesUploadForm.updateFailed" />
              </p>
            )}
          </div>

          <div className={css.formActions}>
            <SecondaryButton
              className={css.cancelButton}
              onClick={onClose}
              disabled={updateInProgress}
            >
              <FormattedMessage id="BehindTheScenesUploadForm.cancel" />
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              inProgress={updateInProgress}
              disabled={!hasNewVideos || isUploadingAny || updateInProgress}
            >
              <FormattedMessage id="BehindTheScenesUploadForm.save" />
            </PrimaryButton>
          </div>
        </form>
      )}
    />
  );
};

BehindTheScenesUploadForm.defaultProps = {
  uploadedVideos: {},
  uploadVideoError: null,
  updateError: null,
};

BehindTheScenesUploadForm.propTypes = {
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  uploadedVideos: object,
  onVideoUpload: func.isRequired,
  onRemoveVideo: func.isRequired,
  onSubmit: func.isRequired,
  uploadInProgress: bool.isRequired,
  uploadVideoError: propTypes.error,
  updateInProgress: bool.isRequired,
  updateError: propTypes.error,
  onClose: func.isRequired,
};

export default injectIntl(BehindTheScenesUploadForm);
